import { computed } from 'vue'
import type {
  AdminDashboardSection,
  DashboardTile,
  NavItem,
  NavSection,
  QuickActionDef,
} from '@/config/navigation'
import {
  adminDashboardSections,
  accountingDashboardTiles,
  quickActions,
  settingsLinks,
  sidebarSections,
} from '@/config/navigation'
import { usePermissions } from '@/composables/usePermissions'
import { useMilestonesStore } from '@/stores/milestones'
import { usePlanUsageStore } from '@/stores/planUsage'
import type { GateId } from '@/types/milestones'

export interface NavItemWithLock extends NavItem {
  gateLocked?: boolean
  featureLocked?: boolean
  /** i18n key for the entitlement lock tooltip — set only when `featureLocked`. */
  featureHintKey?: string
}

export interface NavSectionWithLock extends NavSection {
  items: NavItemWithLock[]
}

function normalizePermissions(permission?: string | string[]): string[] {
  if (!permission) return []
  return Array.isArray(permission) ? permission : [permission]
}

export function canAccess(
  permission: string | string[] | undefined,
  hasPermission: (name: string) => boolean,
): boolean {
  const required = normalizePermissions(permission)
  if (required.length === 0) return true
  return required.some((perm) => hasPermission(perm))
}

export function filterNavItems(
  items: NavItem[],
  hasPermission: (name: string) => boolean,
  inheritedPermission?: string | string[],
): NavItem[] {
  const filtered: NavItem[] = []

  for (const item of items) {
    if (item.kind === 'heading') {
      filtered.push(item)
      continue
    }

    const effectivePermission = item.permission ?? inheritedPermission

    if (item.children?.length) {
      if (effectivePermission && !canAccess(effectivePermission, hasPermission)) {
        continue
      }
      const childInherit = item.permission ?? inheritedPermission
      const children = filterNavItems(item.children, hasPermission, childInherit)
      const childLinks = children.filter((child) => child.kind !== 'heading')
      if (childLinks.length === 0) continue
      filtered.push({ ...item, children })
      continue
    }

    if (!canAccess(effectivePermission, hasPermission)) continue
    filtered.push(item)
  }

  return filtered.filter((item, index, list) => {
    if (item.kind !== 'heading') return true
    const next = list[index + 1]
    return next !== undefined && next.kind !== 'heading'
  })
}

export function isNavItemGateLocked(
  item: NavItem,
  isGateOpen: (gate?: GateId) => boolean,
  inheritedGate?: GateId,
): boolean {
  const required = item.gate ?? inheritedGate
  if (!required) return false
  return !isGateOpen(required)
}

export function annotateNavItemsWithGateLocks(
  items: NavItem[],
  isGateOpen: (gate?: GateId) => boolean,
  inheritedGate?: GateId,
): NavItemWithLock[] {
  const annotated: NavItemWithLock[] = []

  for (const item of items) {
    if (item.kind === 'heading') {
      annotated.push(item)
      continue
    }

    const childInherit = item.gate ?? inheritedGate
    const gateLocked = isNavItemGateLocked(item, isGateOpen, inheritedGate)

    if (item.children?.length) {
      const children = annotateNavItemsWithGateLocks(item.children, isGateOpen, childInherit)
      annotated.push({ ...item, children, gateLocked })
      continue
    }

    annotated.push({ ...item, gateLocked })
  }

  return annotated
}

export function annotateNavSectionsWithGateLocks(
  sections: NavSection[],
  isGateOpen: (gate?: GateId) => boolean,
): NavSectionWithLock[] {
  return sections.map((section) => ({
    ...section,
    items: annotateNavItemsWithGateLocks(section.items, isGateOpen),
  }))
}

/** Reads the tenant plan snapshot: true only when the feature is explicitly not entitled. */
export type FeatureDisabledPredicate = (feature?: string) => boolean

/**
 * Entitlement lock — independent of the milestone gate. An item is locked only
 * when it declares a `feature` the plan snapshot reports as disabled; `feature`
 * is not inherited by children, so set it on each item that needs it.
 */
export function annotateNavItemsWithFeatureLocks(
  items: NavItemWithLock[],
  isFeatureDisabled: FeatureDisabledPredicate,
): NavItemWithLock[] {
  return items.map((item) => {
    const featureLocked = item.feature !== undefined && isFeatureDisabled(item.feature)
    const children = item.children?.length
      ? annotateNavItemsWithFeatureLocks(item.children as NavItemWithLock[], isFeatureDisabled)
      : item.children

    return {
      ...item,
      children,
      featureLocked,
      featureHintKey: featureLocked ? `featureLocked.${item.feature}.navHint` : undefined,
    }
  })
}

export function partitionNavItemsByGate(
  items: NavItemWithLock[],
): { unlocked: NavItemWithLock[]; locked: NavItemWithLock[] } {
  const unlocked: NavItemWithLock[] = []
  const locked: NavItemWithLock[] = []

  for (const item of items) {
    if (item.gateLocked) {
      locked.push(item)
    } else {
      unlocked.push(item)
    }
  }

  return { unlocked, locked }
}

export function filterNavSections(
  sections: NavSection[],
  hasPermission: (name: string) => boolean,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: filterNavItems(section.items, hasPermission),
    }))
    .filter((section) => section.items.length > 0)
}

export function filterDashboardTiles(
  tiles: DashboardTile[],
  hasPermission: (name: string) => boolean,
  isFeatureDisabled: FeatureDisabledPredicate = () => false,
): DashboardTile[] {
  return tiles.filter(
    (tile) =>
      canAccess(tile.permission, hasPermission) &&
      (tile.feature === undefined || !isFeatureDisabled(tile.feature)),
  )
}

export function filterQuickActions(
  actions: QuickActionDef[],
  hasPermission: (name: string) => boolean,
): QuickActionDef[] {
  return actions.filter((action) => hasPermission(action.permission))
}

export function filterAdminDashboardSections(
  sections: AdminDashboardSection[],
  hasPermission: (name: string) => boolean,
  isFeatureDisabled: FeatureDisabledPredicate = () => false,
): AdminDashboardSection[] {
  return sections
    .map((section) => {
      const tiles = filterDashboardTiles(section.tiles ?? [], hasPermission, isFeatureDisabled)
      const groups = (section.groups ?? [])
        .map((group) => ({
          ...group,
          tiles: filterDashboardTiles(group.tiles, hasPermission, isFeatureDisabled),
        }))
        .filter((group) => group.tiles.length > 0)

      return { ...section, tiles, groups }
    })
    .filter(
      (section) =>
        (section.tiles?.length ?? 0) > 0 || (section.groups?.length ?? 0) > 0,
    )
}

export function useFilteredNavigation() {
  const { hasPermission } = usePermissions()
  const milestonesStore = useMilestonesStore()
  const planUsageStore = usePlanUsageStore()

  // The entitlement snapshot drives the locks below, so it is fetched once per
  // session here. `ensureLoaded` never rejects — an unknown snapshot renders unlocked.
  void planUsageStore.ensureLoaded()

  const isFeatureDisabled: FeatureDisabledPredicate = (feature) =>
    feature !== undefined && planUsageStore.featureState(feature) === 'disabled'

  const menuSections = computed(() => {
    const rbacFiltered = filterNavSections(sidebarSections, hasPermission)
    const gateAnnotated = milestonesStore.showAllFeatures
      ? rbacFiltered.map((section) => ({
          ...section,
          items: section.items.map((item) => ({ ...item, gateLocked: false })),
        }))
      : annotateNavSectionsWithGateLocks(rbacFiltered, milestonesStore.isGateOpen)

    return gateAnnotated.map((section) => ({
      ...section,
      items: annotateNavItemsWithFeatureLocks(section.items, isFeatureDisabled),
    }))
  })

  const lockedMenuSections = computed(() =>
    menuSections.value.map((section) => ({
      ...section,
      items: section.items.filter((item) => item.gateLocked || item.featureLocked),
    })).filter((section) => section.items.length > 0),
  )

  const visibleQuickActions = computed(() =>
    filterQuickActions(quickActions, hasPermission),
  )

  const visibleAccountingTiles = computed(() =>
    filterDashboardTiles(accountingDashboardTiles, hasPermission, isFeatureDisabled),
  )

  const visibleAdminSections = computed(() =>
    filterAdminDashboardSections(adminDashboardSections, hasPermission, isFeatureDisabled),
  )

  const visibleSettingsLinks = computed(() =>
    filterDashboardTiles(settingsLinks, hasPermission, isFeatureDisabled),
  )

  return {
    menuSections,
    lockedMenuSections,
    visibleQuickActions,
    visibleAccountingTiles,
    visibleAdminSections,
    visibleSettingsLinks,
    hasPermission,
    showAllFeatures: computed(() => milestonesStore.showAllFeatures),
  }
}
